import axios from 'axios';

import { KanzhongBackend } from 'constant/KanzhongBackend';
import { ServerError, createApiError } from 'stores/ApiClientError';

// --- 型定義 ---

export type CreateMemberParams = {
  memberName: string;
  memberNameKana: string;
  employmentTypeId: string;
  userId?: string;
  genderId: string;
  isIndividual: boolean;
  department?: string;
  partnerId?: string;
  contractTypeId?: string;
  remarks?: string;
};

export type UpdateMemberParams = {
  memberId: string;
  memberName: string;
  memberNameKana: string;
  genderId: string;
  userId?: string;
  department?: string;
  contractTypeId?: string;
  remarks?: string;
};

export type DeleteMemberParams = {
  memberId: string;
};

export type GetMemberParams = {
  memberId: string;
};

export type ListMemberParams = {
  memberName?: string;
  memberNameKana?: string;
  employmentTypeId?: string;
  partnerId?: string;
  isIndividual?: boolean;
  contractTypeId?: string;
  includeDeleted?: boolean;
};

export type MemberPartnerResult = {
  partnerId: string;
  partnerName: string;
  partnerNameKana: string;
  partnerTypeId: string;
  corporateNumber?: string;
  invoiceNumber?: string;
  address?: string;
  phoneNumber?: string;
  mailAddress?: string;
  contactName?: string;
  contractStartDate: string;
  contractEndDate?: string;
  remarks?: string;
  createdAt: string;
  createdBy: string;
  createdByName: string;
};

export type MemberDetailResult = {
  memberId: string;
  memberName: string;
  memberNameKana: string;
  employmentTypeId: string;
  userId?: string;
  mailAddress?: string;
  genderId: string;
  isIndividual: boolean;
  department?: string;
  partnerId?: string;
  contractTypeId: string;
  remarks?: string;
  isDeleted: boolean;
  createdAt: string;
  createdBy: string;
  createdByName: string;
  partner?: MemberPartnerResult;
};

export type MemberListResult = {
  memberId: string;
  memberName: string;
  memberNameKana: string;
  employmentTypeId: string;
  userId?: string;
  mailAddress?: string;
  genderId: string;
  isIndividual: boolean;
  department?: string;
  partnerId?: string;
  partnerName?: string;
  partnerNameKana?: string;
  contractTypeId: string;
  remarks?: string;
  isDeleted: boolean;
  createdAt: string;
  createdBy: string;
  createdByName: string;
};

// --- API関数 ---

/**
 * 要員登録API
 */
export const createMember = async (params: CreateMemberParams): Promise<string> => {
  const sessionId = localStorage.getItem('session-id');

  try {
    const response = await axios.post(KanzhongBackend.CREATE_MEMBER, params, {
      headers: {
        'X-Session-Id': sessionId,
      },
    });

    return response.data.memberId;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * 要員更新API
 */
export const updateMember = async (params: UpdateMemberParams): Promise<void> => {
  const sessionId = localStorage.getItem('session-id');

  try {
    await axios.post(KanzhongBackend.UPDATE_MEMBER, params, {
      headers: {
        'X-Session-Id': sessionId,
      },
    });
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * 要員削除API（論理削除）
 */
export const deleteMember = async (params: DeleteMemberParams): Promise<void> => {
  const sessionId = localStorage.getItem('session-id');

  try {
    await axios.post(KanzhongBackend.DELETE_MEMBER, params, {
      headers: {
        'X-Session-Id': sessionId,
      },
    });
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * 要員取得API（削除済みも取得可能）
 */
export const getMember = async (params: GetMemberParams): Promise<MemberDetailResult> => {
  const sessionId = localStorage.getItem('session-id');

  try {
    const response = await axios.post(KanzhongBackend.GET_MEMBER, params, {
      headers: {
        'X-Session-Id': sessionId,
      },
    });

    return response.data.result;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * 要員一覧取得API
 */
export const listMember = async (params: ListMemberParams): Promise<MemberListResult[]> => {
  const sessionId = localStorage.getItem('session-id');

  try {
    const response = await axios.post(KanzhongBackend.LIST_MEMBER, params, {
      headers: {
        'X-Session-Id': sessionId,
      },
    });

    return response.data.results;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};
